import { MouseEventHandler, useState } from "react"
import { IoKey, IoTrash } from "react-icons/io5"
import { Button, Flex, Link, Tag, Tooltip, useColorModeValue } from "@chakra-ui/react"
import { MdAdd, MdCheck, MdCopyAll, MdDownload, MdEdit, MdLink, MdLock, MdNoteAdd, MdPersonAdd, MdRemove, MdSend, MdSendAndArchive, MdSwapHoriz, MdVisibility, MdUndo } from "react-icons/md"
import { AssessmentStatus, IAssessment } from "@bemap/utils"
import { CertificateStatus, ICertificate } from "@bemap/utils"
import { IInvoice, InvoiceStatus } from "@bemap/utils"
import { useTranslation } from "react-i18next"

interface LoadingStates {
  [key: string]: boolean;
}

export const AssessmentActions = ({
  report,
  generateAction,
  resendAction,
  downloadAction,
  convertAction,
  mailAction,
  copyAction,
  viewAction,
}: {
  report: IAssessment
  generateAction?: MouseEventHandler<HTMLButtonElement>
  resendAction?: MouseEventHandler<HTMLButtonElement>
  downloadAction?: MouseEventHandler<HTMLButtonElement>
  convertAction?: MouseEventHandler<HTMLButtonElement>
  mailAction?: MouseEventHandler<HTMLButtonElement>
  copyAction?: MouseEventHandler<HTMLButtonElement>
  viewAction?: MouseEventHandler<HTMLButtonElement>
}) => {
  const blueColor = useColorModeValue('blue.900', 'white')
  const borderColor = useColorModeValue('gray.200', 'gray.500')
  const { t } = useTranslation()

  function RenderIcon(status: AssessmentStatus) {
    const [loadingStates, setLoadingStates] = useState<LoadingStates>({
      resend: false,
      generate: false,
      convert: false,
      download: false,
      mail: false,
      copy: false,
      view: false,
    });

    const handleButtonClick = async (action: any, loadingKey: keyof LoadingStates) => {
      setLoadingStates(prev => ({ ...prev, [loadingKey]: true }));
      try {
        await action();
      } finally {
        setLoadingStates(prev => ({ ...prev, [loadingKey]: false }));
      }
    };

    switch (status) {
      case AssessmentStatus.WAITING:
        return (
          <Flex direction={'row'} gap={'8px'}>
            <Tooltip label={t('analyst.assesments_action_resend')}>
              <Button
                borderColor={borderColor}
                color={blueColor}
                size='sm'
                variant='outline'
                onClick={() => handleButtonClick(resendAction, 'resend')}
                isLoading={loadingStates.resend}
              >
                <MdSend />
              </Button>
            </Tooltip>
            <Tooltip label={t('analyst.assesments_action_copy')}>
              <Button
                borderColor={borderColor}
                color={blueColor}
                size='sm'
                variant='outline'
                onClick={() => handleButtonClick(copyAction, 'copy')}
                isLoading={loadingStates.copy}
              >
                <MdCopyAll />
              </Button>
            </Tooltip>
          </Flex>
        )
      case AssessmentStatus.DONE:
        if (report?.results?.status === 'error') {
          return <Tooltip label={t('analyst.assesments_insufficient_answers')}>
            <Tag size='sm' colorScheme={'red'}>Indisponível</Tag>
          </Tooltip>
        } else {
          return (
            <Flex direction={'row'} gap={'8px'}>
              <Tooltip label={t('analyst.assesments_action_generate')}>
                <Button
                  borderColor={borderColor}
                  color={blueColor}
                  size='sm'
                  variant='outline'
                  onClick={() => handleButtonClick(generateAction, 'generate')}
                  isLoading={loadingStates.generate}
                >
                  <MdNoteAdd />
                </Button>
              </Tooltip>
              {`${report.report_id}` === '1' &&
                <Tooltip label={t('analyst.assesments_action_convert_ext')}>
                  <Button
                    borderColor={borderColor}
                    color={blueColor}
                    size='sm'
                    variant='outline'
                    onClick={() => handleButtonClick(convertAction, 'convert')}
                    isLoading={loadingStates.convert}
                  >
                    <MdLock />
                  </Button>
                </Tooltip>
              }
              {`${report.report_id}` === '2' &&
                <Tooltip label={t('analyst.assesments_action_convert_simp')}>
                  <Button
                    borderColor={borderColor}
                    color={blueColor}
                    size='sm'
                    variant='outline'
                    onClick={() => handleButtonClick(convertAction, 'convert')}
                    isLoading={loadingStates.convert}
                  >
                    <MdSwapHoriz />
                  </Button>
                </Tooltip>
              }
            </Flex>
          )
        }
      case AssessmentStatus.GENERATED:
        return (
          <Flex direction={'row'} gap={'8px'}>
            <Tooltip label={t('analyst.assesments_action_view')}>
              <Button
                borderColor={borderColor}
                color={blueColor}
                size='sm'
                variant='outline'
                onClick={() => handleButtonClick(viewAction, 'view')}
                isLoading={loadingStates.view}
              >
                <MdVisibility />
              </Button>
            </Tooltip>
            <Tooltip label={t('analyst.assesments_action_download')}>
              <Button
                borderColor={borderColor}
                color={blueColor}
                size='sm'
                variant='outline'
                onClick={() => handleButtonClick(downloadAction, 'download')}
                isLoading={loadingStates.download}
              >
                <MdDownload />
              </Button>
            </Tooltip>
            <Tooltip label={t('analyst.assesments_action_send')}>
              <Button
                borderColor={borderColor}
                color={blueColor}
                size='sm'
                variant='outline'
                onClick={() => handleButtonClick(mailAction, 'mail')}
                isLoading={loadingStates.mail}
              >
                <MdSendAndArchive />
              </Button>
            </Tooltip>
            {`${report.report_id}` === '1' &&
              <Tooltip label={t('analyst.assesments_action_convert_ext')}>
                <Button
                  borderColor={borderColor}
                  color={blueColor}
                  size='sm'
                  variant='outline'
                  onClick={() => handleButtonClick(convertAction, 'convert')}
                  isLoading={loadingStates.convert}
                >
                  <MdLock />
                </Button>
              </Tooltip>
            }
          </Flex>
        )
      default:
        return null
    }
  }

  return (
    <Flex gap='8px' className="noprint">
      {RenderIcon(report.status as AssessmentStatus)}
    </Flex>
  )
}

export const InvoiceActions = ({
  invoice,
  payAction,
  stripeAction,
  viewAction,
  returnToUnpaidAction,
}: {
  invoice: IInvoice
  payAction?: MouseEventHandler<HTMLButtonElement>
  stripeAction?: MouseEventHandler<HTMLButtonElement>
  viewAction?: MouseEventHandler<HTMLButtonElement>
  returnToUnpaidAction?: MouseEventHandler<HTMLButtonElement>
}) => {
  const { t } = useTranslation()
  const blueColor = useColorModeValue('blue.900', 'white')
  const borderColor = useColorModeValue('gray.200', 'gray.500')

  function RenderIcon(status: InvoiceStatus) {
    const [loadingStates, setLoadingStates] = useState<LoadingStates>({
      stripe: false,
      pay: false,
      view: false,
      returnToUnpaid: false,
    });

    const handleButtonClick = async (action: any, loadingKey: keyof LoadingStates) => {
      setLoadingStates(prev => ({ ...prev, [loadingKey]: true }));
      try {
        await action();
      } finally {
        setLoadingStates(prev => ({ ...prev, [loadingKey]: false }));
      }
    };

    switch (status) {
      case InvoiceStatus.OPEN:
        return (
          <Flex direction={'row'} gap={'8px'}>
            {invoice.hosted_invoice_url ? (
              <Tooltip label={t('actions.invoice_open')}>
                <Button
                  borderColor={borderColor}
                  color={blueColor}
                  size='sm'
                  variant='outline'
                  as={Link}
                  href={invoice.hosted_invoice_url}
                  target="_blank"
                >
                  <MdLink />
                </Button>
              </Tooltip>
            ) : (
              <Tooltip label={t('actions.invoice_send')}>
                <Button
                  borderColor={borderColor}
                  color={blueColor}
                  size='sm'
                  variant='outline'
                  onClick={() => handleButtonClick(stripeAction, 'stripe')}
                  isLoading={loadingStates.stripe}
                >
                  <MdSend />
                </Button>
              </Tooltip>
            )}
            <Tooltip label={t('actions.invoice_pay')}>
              <Button
                borderColor={borderColor}
                color={blueColor}
                size='sm'
                variant='outline'
                onClick={() => handleButtonClick(payAction, 'pay')}
                isLoading={loadingStates.pay}
              >
                <MdCheck />
              </Button>
            </Tooltip>
          </Flex>
        )
      case InvoiceStatus.PAID:
        return (
          <Flex direction={'row'} gap={'8px'}>
            <Tooltip label={t('actions.invoice_view')}>
              <Button
                borderColor={borderColor}
                color={blueColor}
                size='sm'
                variant='outline'
                onClick={() => handleButtonClick(viewAction, 'view')}
                isLoading={loadingStates.view}
              >
                <MdVisibility />
              </Button>
            </Tooltip>
            <Tooltip label={t('actions.invoice_return_to_unpaid')}>
              <Button
                borderColor={borderColor}
                color={blueColor}
                size='sm'
                variant='outline'
                onClick={() => handleButtonClick(returnToUnpaidAction, 'returnToUnpaid')}
                isLoading={loadingStates.returnToUnpaid}
              >
                <MdUndo />
              </Button>
            </Tooltip>
          </Flex>
        )
      default:
        return null
    }
  }

  return (
    <Flex gap='8px' className="noprint">
      {RenderIcon(invoice.status as InvoiceStatus)}
    </Flex>
  )
}

export const CertificateActions = ({
  report,
  generateAction,
  downloadAction,
}: {
  report: ICertificate
  generateAction?: MouseEventHandler<HTMLButtonElement>
  downloadAction?: MouseEventHandler<HTMLButtonElement>
}) => {
  const blueColor = useColorModeValue('blue.900', 'white')
  const borderColor = useColorModeValue('gray.200', 'gray.500')
  function RenderIcon(status: CertificateStatus) {
    const [loadingStates, setLoadingStates] = useState<LoadingStates>({
      resend: false,
      generate: false,
      convert: false,
      download: false,
    });

    const { t } = useTranslation()

    const handleButtonClick = async (action: any, loadingKey: keyof LoadingStates) => {
      setLoadingStates(prev => ({ ...prev, [loadingKey]: true }));
      try {
        await action();
      } finally {
        setLoadingStates(prev => ({ ...prev, [loadingKey]: false }));
      }
    };

    switch (status) {
      case CertificateStatus.WAITING:
        return (
          <Flex direction={'row'} gap={'8px'}>
            <Tooltip label={t('partner.certificates_action_generate')}>
              <Button
                borderColor={borderColor}
                color={blueColor}
                size='sm'
                variant='outline'
                onClick={() => handleButtonClick(generateAction, 'generate')}
                isLoading={loadingStates.generate}
              >
                <MdLock />
              </Button>
            </Tooltip>
          </Flex>
        )
      case CertificateStatus.GENERATED:
        return (
          <Tooltip label={t('partner.certificates_action_download')}>
            <Button
              isDisabled={!report.key}
              borderColor={borderColor}
              color={blueColor}
              size='sm'
              variant='outline'
              onClick={() => handleButtonClick(downloadAction, 'download')}
              isLoading={loadingStates.download}
            >
              <MdDownload />
            </Button>
          </Tooltip>
        )
      default:
        return null
    }
  }

  return (
    <Flex gap='8px' className="noprint">
      {RenderIcon(report.status as CertificateStatus)}
    </Flex>
  )
}

export const TableRowActions = ({
  viewAction,
  editAction,
  deleteAction,
  addAction,
  retrieveAction,
  newAssesmentAction,
  resetAction,
  transformToPartnerAction,
  downloadAction,
}: {
  viewAction?: MouseEventHandler<HTMLButtonElement>
  editAction?: MouseEventHandler<HTMLButtonElement>
  deleteAction?: MouseEventHandler<HTMLButtonElement>
  addAction?: MouseEventHandler<HTMLButtonElement>
  retrieveAction?: MouseEventHandler<HTMLButtonElement>
  newAssesmentAction?: MouseEventHandler<HTMLButtonElement>
  resetAction?: MouseEventHandler<HTMLButtonElement>
  transformToPartnerAction?: MouseEventHandler<HTMLButtonElement>
  downloadAction?: MouseEventHandler<HTMLButtonElement>
}) => {
  const blueColor = useColorModeValue('blue.900', 'white')
  const redColor = useColorModeValue('red.500', 'red.200')
  const borderColor = useColorModeValue('gray.200', 'gray.500')
  const { t } = useTranslation()

  const [loadingStates, setLoadingStates] = useState<LoadingStates>({
    view: false,
    edit: false,
    delete: false,
    add: false,
    retrieve: false,
    reset: false,
    newAssesment: false,
    transformToPartner: false,
  });

  const handleButtonClick = async (action: any, loadingKey: keyof LoadingStates) => {
    setLoadingStates(prev => ({ ...prev, [loadingKey]: true }));
    try {
      await action();
    } finally {
      setLoadingStates(prev => ({ ...prev, [loadingKey]: false }));
    }
  };

  return (
    <Flex gap='8px' className="noprint">
      {addAction && (
        <Tooltip label={t('actions.add_credits')}>
          <Button borderColor={borderColor} color={blueColor} size='sm' variant='outline' onClick={() => handleButtonClick(addAction, 'add')}
            isLoading={loadingStates.add}>
            <MdAdd />
          </Button>
        </Tooltip>
      )}
      {retrieveAction && (
        <Tooltip label={t('actions.retrieve_credits')}>
          <Button borderColor={borderColor} color={blueColor} size='sm' variant='outline' onClick={() => handleButtonClick(retrieveAction, 'retrieve')}
            isLoading={loadingStates.retrieve}>
            <MdRemove />
          </Button>
        </Tooltip>
      )}
      {viewAction && (
        <Tooltip label={t('actions.view')}>
          <Button borderColor={borderColor} color={blueColor} size='sm' variant='outline' onClick={() => handleButtonClick(viewAction, 'view')}
            isLoading={loadingStates.view}>
            <MdVisibility />
          </Button>
        </Tooltip>
      )}
      {newAssesmentAction && (
        <Tooltip label={t('analyst.assesments_new')}>
          <Button
            borderColor={borderColor}
            color={blueColor}
            size='sm'
            variant='outline'
            onClick={() => handleButtonClick(newAssesmentAction, 'newAssessment')}
            isLoading={loadingStates.newAssessment}
          >
            <MdSend />
          </Button>
        </Tooltip>
      )}
      {editAction && (
        <Tooltip label={t('actions.edit')}>
          <Button borderColor={borderColor} color={blueColor} size='sm' variant='outline' onClick={() => handleButtonClick(editAction, 'edit')}
            isLoading={loadingStates.edit}>
            <MdEdit />
          </Button>
        </Tooltip>
      )}
      {resetAction && (
        <Tooltip label={t('auth.forgot_password_title_reset')}>
          <Button borderColor={borderColor} color={blueColor} size='sm' variant='outline' onClick={() => handleButtonClick(resetAction, 'reset')}
            isLoading={loadingStates.reset}>
            <IoKey />
          </Button>
        </Tooltip>
      )}
      {downloadAction && (
        <Tooltip label={t('partner.certificates_action_download')}>
          <Button borderColor={borderColor} color={blueColor} size='sm' variant='outline' onClick={() => handleButtonClick(downloadAction, 'download')}
            isLoading={loadingStates.download}>
            <MdDownload />
          </Button>
        </Tooltip>
      )}
      {deleteAction && (
        <Tooltip label={t('actions.delete')}>
          <Button borderColor={borderColor} color={redColor} size='sm' variant='outline' onClick={() => handleButtonClick(deleteAction, 'delete')}
            isLoading={loadingStates.delete}>
            <IoTrash />
          </Button>
        </Tooltip>
      )}
      {transformToPartnerAction && (
        <Tooltip label={t('actions.transform_to_partner')}>
          <Button borderColor={borderColor} color={blueColor} size='sm' variant='outline' onClick={() => handleButtonClick(transformToPartnerAction, 'transformToPartner')}
            isLoading={loadingStates.transformToPartner}>
            <MdPersonAdd />
          </Button>
        </Tooltip>
      )}
    </Flex>
  )
}